<script>
  // The want page's shelf (spec "Wants" > Shelf; VISUALS.md "Rows of tiles
  // are carousels"): square bundle tiles in one row that runs off the
  // screen's right edge. A phone swipes it; a desktop gets round chevrons
  // that page by however many tiles fit, shown only when there is more that
  // way. Order: the Default Want List (a thin divider after it), each Project
  // (source badge, commander as the fan's face, `Name (count)`), then the New
  // project tile with the slots left (it opens the New project modal; none
  // left, it is disabled). A long press / right-click / menu key on a
  // Project opens its menu (the page renders both). Each bundle arrives
  // with its fan and badge already computed (want-page-logic.buildShelf).
  import { untrack } from 'svelte';
  import { getCardImageUrl } from '../../lib/services/card-utils.js';
  import { menupress } from '../../lib/actions/menupress.js';
  import { titleParts, shelfStep, shelfEdges } from '../../lib/logic/want-page-logic.js';

  /** @typedef {import('../../lib/logic/want-page-logic.js').Bundle} Bundle */

  /**
   * `newProject` is the owner's New project tile (slots left, and what opens
   * its modal); a visitor's shelf has none.
   * @type {{
   *   bundles: Bundle[],
   *   pickedId: string,
   *   onPick: (id: string) => void,
   *   onMenu: (id: string, anchor: HTMLElement) => void,
   *   newProject?: { slotsLeft: number, onOpen: () => void } | null,
   * }}
   */
  let { bundles, pickedId, onPick, onMenu, newProject = null } = $props();

  let wrap = $state(/** @type {HTMLElement | null} */ (null));
  let shelf = $state(/** @type {HTMLElement | null} */ (null));
  let canPrev = $state(false);
  let canNext = $state(false);
  // Tiles run off the SCREEN's edges, not the page column's: the shelf
  // reaches past the column on both sides and padding puts the first tile
  // back in the column.
  let bleedL = $state(0);
  let bleedR = $state(0);

  function updateEdges() {
    if (!shelf) return;
    ({ canPrev, canNext } = shelfEdges({
      scrollLeft: shelf.scrollLeft,
      clientWidth: shelf.clientWidth,
      scrollWidth: shelf.scrollWidth,
      endPadding: parseFloat(getComputedStyle(shelf).paddingRight) || 0,
    }));
  }

  function measure() {
    const parent = wrap?.parentElement?.getBoundingClientRect();
    if (parent) {
      bleedL = Math.max(0, parent.left);
      bleedR = Math.max(0, document.documentElement.clientWidth - parent.right);
    }
    updateEdges();
  }

  $effect(() => {
    void bundles.length; // tiles added / removed move the edges
    untrack(measure);
  });

  // The picked bundle is always in view (a just-added Project lands off-screen).
  $effect(() => {
    const id = pickedId;
    untrack(() => {
      const tiles = /** @type {HTMLElement[]} */ ([
        ...(shelf?.querySelectorAll('[data-bundle]') ?? []),
      ]);
      tiles
        .find((t) => t.dataset.bundle === id)
        ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  });

  /** @param {1 | -1} dir */
  function page(dir) {
    if (!shelf) return;
    const tile = /** @type {HTMLElement | null} */ (shelf.querySelector('.bundle:not(.builtin)'));
    const gap = parseFloat(getComputedStyle(shelf).columnGap) || 0;
    const left =
      dir * shelfStep({ viewport: shelf.clientWidth, tile: tile?.offsetWidth ?? 0, gap });
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    shelf.scrollBy({ left, behavior: still ? 'auto' : 'smooth' });
  }
</script>

<svelte:window onresize={measure} />

{#snippet tile(/** @type {Bundle} */ b)}
  {@const parts = titleParts(b.name)}
  <span class="stack">
    {#each b.fan as f, i (f.key)}
      <img
        src={getCardImageUrl({ scryfallId: f.scryfallId }, 'small')}
        alt=""
        class:front={f.front}
        style:--i={i}
        loading="lazy"
      />
    {:else}
      <span class="stack-ghost"></span><span class="stack-ghost"></span>
    {/each}
    {#if b.site}
      <span class="src-badge" title={b.site.name}><img src={b.site.icon} alt="" /></span>
    {/if}
  </span>
  <span class="b-name"
    >{parts.head}<span class="tail"
      >{parts.tail}{#if b.count}&nbsp;<span class="b-count">({b.count})</span>{/if}</span
    ></span
  >
{/snippet}

<div
  class="shelf-wrap"
  bind:this={wrap}
  style:margin-left="-{bleedL}px"
  style:margin-right="-{bleedR}px"
>
  {#if canPrev}
    <button class="chev prev" aria-label="Previous projects" onclick={() => page(-1)}
      >&lsaquo;</button
    >
  {/if}
  <ul
    class="shelf"
    aria-label="Want list and projects"
    bind:this={shelf}
    onscroll={updateEdges}
    style:padding-left="{bleedL + 4}px"
    style:padding-right="{bleedR + 4}px"
    style:scroll-padding-left="{bleedL + 4}px"
  >
    {#each bundles as b (b.id)}
      <li class="bundle" class:builtin={b.kind === 'default'} data-bundle={b.id}>
        {#if b.kind === 'project'}
          <button
            class="b-hit"
            class:on={b.id === pickedId}
            aria-pressed={b.id === pickedId}
            onclick={() => onPick(b.id)}
            use:menupress={(el) => onMenu(b.id, el)}
          >
            {@render tile(b)}
          </button>
        {:else}
          <button
            class="b-hit"
            class:on={b.id === pickedId}
            aria-pressed={b.id === pickedId}
            onclick={() => onPick(b.id)}
          >
            {@render tile(b)}
          </button>
        {/if}
      </li>
    {/each}
    {#if newProject}
      {@const slotsLeft = newProject.slotsLeft}
      <li class="bundle new">
        <button
          class="b-hit plus"
          class:full={slotsLeft === 0}
          disabled={slotsLeft === 0}
          onclick={newProject.onOpen}
        >
          <span class="stack" aria-hidden="true">
            <span class="stack-ghost"></span><span class="stack-ghost"></span>
            <span class="plus-mark">+</span>
          </span>
          <span class="b-name"
            >New <span class="tail"
              >project&nbsp;<span class="b-count">({slotsLeft} left)</span></span
            ></span
          >
        </button>
      </li>
    {/if}
  </ul>
  {#if canNext}
    <button class="chev next" aria-label="More projects" onclick={() => page(1)}>&rsaquo;</button>
  {/if}
</div>

<style>
  .shelf-wrap {
    position: relative;
  }
  .shelf {
    list-style: none;
    margin: 0;
    padding: 4px;
    display: flex;
    gap: 10px;
    overflow-x: auto;
    scroll-snap-type: x proximity;
    scrollbar-width: none;
  }
  .shelf::-webkit-scrollbar {
    display: none;
  }
  .bundle {
    flex-shrink: 0;
    scroll-snap-align: start;
  }
  /* The Default Want List stands apart from the Projects: a thin divider. */
  .bundle.builtin {
    margin-right: 12px;
    padding-right: 22px;
    border-right: 1px solid var(--color-border);
  }
  .chev {
    position: absolute;
    z-index: 5;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    border: 1px solid var(--color-border);
    border-radius: 50%;
    background: var(--color-bg-surface);
    box-shadow: 0 2px 8px rgba(44, 36, 25, 0.2);
    font-size: 24px;
    line-height: 1;
    color: var(--color-text);
    cursor: pointer;
  }
  .chev.prev {
    left: 12px;
  }
  .chev.next {
    right: 12px;
  }
  /* A phone swipes; no chevrons. */
  @media (hover: none) and (pointer: coarse) {
    .chev {
      display: none;
    }
  }
  /* Every shelf tile is square. A long press is ours (the Project menu), so
     no text selection or iOS callout. */
  .b-hit {
    -webkit-touch-callout: none;
    user-select: none;
    width: 156px;
    aspect-ratio: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px;
    overflow: hidden;
    border: 1px solid var(--color-border);
    border-radius: 14px;
    background: var(--color-bg-tray);
    font: inherit;
    text-align: center;
    cursor: pointer;
  }
  .b-hit:active:not(:disabled) {
    transform: scale(0.98);
  }
  .b-hit.on {
    background: var(--color-bg-surface);
    border-color: var(--color-accent-dark);
    box-shadow: 0 0 0 1.5px var(--color-accent-dark);
  }
  /* A fanned hand of three cards: "this is a bundle of cards". */
  .stack {
    position: relative;
    flex-shrink: 0;
    width: 104px;
    height: 92px;
  }
  .stack img,
  .stack-ghost {
    position: absolute;
    left: 28px;
    top: 4px;
    width: 52px;
    height: 73px;
    object-fit: cover;
    border-radius: 3px;
    background: var(--color-accent-bg);
    box-shadow: 0 1px 3px rgba(44, 36, 25, 0.35);
    transform-origin: 50% 110%;
    transform: rotate(calc((var(--i) - 1) * 16deg));
  }
  /* The commander: centre of the fan, on top. */
  .stack img.front {
    z-index: 3;
    box-shadow: 0 2px 6px rgba(44, 36, 25, 0.5);
  }
  .stack-ghost {
    border: 1.5px dashed var(--color-text-muted);
    box-shadow: none;
    background: transparent;
  }
  .stack-ghost:first-child {
    transform: rotate(-10deg);
  }
  .stack-ghost + .stack-ghost {
    transform: rotate(8deg);
  }
  .src-badge {
    position: absolute;
    z-index: 4;
    left: 6px;
    bottom: 2px;
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: var(--color-bg-surface);
    box-shadow: 0 1px 3px rgba(44, 36, 25, 0.35);
  }
  .stack .src-badge img {
    position: static;
    width: 18px;
    height: 18px;
    border-radius: 0;
    box-shadow: none;
    background: none;
    transform: none;
    object-fit: contain;
  }
  .b-name {
    font-weight: 700;
    font-size: 14px;
    line-height: 1.25;
    color: var(--color-text);
    /* Two lines at most, so the tile stays square. */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .tail {
    white-space: nowrap;
  }
  .b-count {
    font-weight: 400;
    color: var(--color-text-tertiary);
  }
  .b-hit.plus {
    border-style: dashed;
    border-color: var(--color-text-muted);
    background: transparent;
  }
  .b-hit.plus:disabled {
    cursor: default;
  }
  .b-hit.plus.full {
    opacity: 0.5;
  }
  .b-hit.plus .b-name {
    color: var(--color-text-secondary);
  }
  .plus-mark {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 34px;
    font-weight: 300;
    color: var(--color-text-secondary);
  }

  @media (max-width: 768px) {
    .shelf {
      gap: 8px;
    }
    .bundle.builtin {
      margin-right: 2px;
      padding-right: 10px;
    }
    .b-hit {
      width: 124px;
      gap: 4px;
      padding: 6px;
    }
    .stack {
      width: 76px;
      height: 64px;
    }
    .stack img,
    .stack-ghost {
      left: 20px;
      width: 36px;
      height: 50px;
    }
    .b-name {
      font-size: 13px;
    }
  }
</style>
